import { ReactElement, useState } from "react";
import {
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,

} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Expand as ExpandIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import DynamicFormRenderer from "./forms/DynamicFormRenderer";
import WaitingScreen from "./WaitingScreen.component";
import Results from "./Results.component";

export interface StepDetailsPanelProps {
  stepId: string;
  stepTitle: string;
  stepDescription: string;
  isActive: boolean;
  isProcessing?: boolean;
  results?: any;
  error?: string;
  onClose?: () => void;
  onBack?: () => void;
  onContinue?: () => void;
  onSubmit?: (data: any) => void;
  onSkip?: (data: any) => void;
  onCancelProcessing?: () => void;
  onResetStep?: () => void;
  className?: string;
  projectContext?: string;
  annotationResult?: any;
  onResampleQc?: (args: {
  step: string;
  seed: number;
  artifactId: string;
  selectedGene?: string;
}) => void;

}

const StepDetailsPanel = ({
  onResampleQc,
  stepId,
  stepTitle,
  stepDescription,
  isActive,
  isProcessing = false,
  results,
  error,
  onClose,
  onBack,
  onContinue,
  onSubmit,
  onSkip,
  onCancelProcessing,
  onResetStep,
  className = "",
  projectContext = "",
  annotationResult,
}: StepDetailsPanelProps): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [parametersExpanded, setParametersExpanded] = useState(false);

  const parameterEntries = Object.entries(results?.parameters || {}).filter(
    ([key]) => !key.startsWith("_") && key !== "fileContent",
  );

  const parameterLabel = (key: string) =>
    key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/^./, (character) => character.toUpperCase());

  const parameterValue = (value: any) => {
    if (value === null) return "None";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFormSubmit = (data: any) => {
    onSubmit?.(data);
  };

  // QC and other single-cell results can use column-oriented tableData or
  // plotSample, not only an array of row objects.
  const hasTableData = Boolean(
    (results?.tableData &&
      ((Array.isArray(results.tableData) && results.tableData.length > 0) ||
        (typeof results.tableData === "object" && Object.keys(results.tableData).length > 0))) ||
    (results?.plotlyData && typeof results.plotlyData === "object") ||
    (results?.plotSample && typeof results.plotSample === "object")
  );

  if (isProcessing) {
    return (
      <div className={`flex-1 p-6 ${className}`}>
        <WaitingScreen
          title={stepTitle}
          message="Your data is being processed"
          instruction="You're free to browse around while your data runs!"
          estimatedTime={stepId === "cell-type-annotation" ? "up to 20 min" : "up to 10 min"}
          onCancel={onResetStep}
          cancelButtonText="Cancel analysis"
        />
      </div>
    );
  }

  // Don't show full-page error, let the form render with error alert

  return (
    <div
      className={`flex-1 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
        borderRadius: "12px",
        border: "1px solid #D9D9D9", // tokens.colors.gray[300]
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200">
        <RadioButtonUncheckedIcon
          sx={{
            color: "#19C1DB",
            fontSize: 24,
          }}
        />
        <Typography
          variant="h5"
          className="text-gray-900"
          sx={{ fontWeight: 600 }}
        >
          {stepTitle}
        </Typography>
      </div>

      {/* Content */}
      <div
        className={`flex-1 p-4 min-h-0 ${
          results
            ? "flex flex-col overflow-hidden"
            : "overflow-y-auto"
        }`}
      >
        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            className="mb-4"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {results && (
          <Box
            component="section"
            aria-label={`Parameters used for ${stepTitle}`}
            sx={{
              mb: 1,
              p: 1,
              flexShrink: 0,
              border: "1px solid #0e7490",
              borderLeftWidth: 4,
              borderRadius: 2,
              backgroundColor: "#ecfeff",
              boxShadow: "0 1px 4px rgba(14, 116, 144, 0.10)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: "#164e63", fontWeight: 700 }}>
                Parameters used · {parameterEntries.length || "No"} saved · Read only
              </Typography>
              {parameterEntries.length > 0 && (
                <Button size="small" onClick={() => setParametersExpanded((expanded) => !expanded)} sx={{ py: 0, minWidth: 0, textTransform: "none", color: "#0e7490", fontWeight: 700 }}>
                  {parametersExpanded ? "Hide" : "View"}
                </Button>
              )}
            </Box>

            {parametersExpanded && parameterEntries.length > 0 && (
              <Box
                component="dl"
                sx={{
                  mt: 1,
                  mb: 0,
                  maxHeight: 160,
                  overflow: "auto",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  gap: 1,
                }}
              >
                {parameterEntries.map(([key, value]) => (
                  <Box key={key} sx={{ minWidth: 0, p: 1, borderRadius: 1, backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
                    <Typography component="dt" variant="caption" sx={{ color: "#0e7490", fontWeight: 700 }}>
                      {parameterLabel(key)}
                    </Typography>
                    <Typography component="dd" variant="body2" sx={{ m: 0, color: "#0f172a", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {parameterValue(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Form or Results */}
        {!results ? (
          <DynamicFormRenderer
            currentStep={stepId}
            dataType="single cell data"
            onSubmit={handleFormSubmit}
            onBack={onBack}
            onContinue={onContinue}
            projectContext={projectContext}
            annotationResult={annotationResult}
          />
        ) : hasTableData ? (
          <Results stepResults={results} currentStep={stepId} onResampleQc={onResampleQc} />
        ) : (
          // Show basic results when available but no tableData
          <div className="space-y-6">
            {/* Results Header */}
            <div className="flex items-center gap-2">
              <Button
                onClick={onBack}
                sx={{
                  minWidth: "auto",
                  p: 0,
                  color: "#6b7280",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#19C1DB",
                  },
                }}
              >
                <ArrowBackIcon />
              </Button>
              <Typography
                variant="h6"
                className="text-gray-800"
                sx={{ fontWeight: 600 }}
              >
                Result
              </Typography>
              <Typography variant="body2" className="text-gray-500">
                {new Date().toLocaleDateString()}
              </Typography>
            </div>

            {/* Success Message */}
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className="bg-green-50 border border-green-200 rounded-lg p-4"
            >
              <CheckCircleIcon sx={{ color: "#10b981", fontSize: 20 }} />
              <Typography variant="body1" className="text-green-800">
                {results.message || "Step completed successfully"}
              </Typography>
            </Box>

            {/* Basic Results Display */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <Typography variant="body2" className="text-gray-700 mb-2">
                Step: {results.currentStep || stepTitle}
              </Typography>
              {results.nextStep && (
                <Typography variant="body2" className="text-gray-700">
                  Next: {results.nextStep}
                </Typography>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {results && (
        <div className="flex justify-between gap-3 p-4 border-t border-gray-200 mt-auto">
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                // Always use back navigation logic, which includes warning modal
                onBack?.();
              }}
              sx={{ textTransform: "none", fontWeight: 500, px: 3, py: 1.5 }}
            >
              Back
            </Button>

          </Box>
          <Button
            variant="contained"
            onClick={onContinue}
            className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1.5,
            }}
          >
            Continue
          </Button>
        </div>
      )}


      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <ExpandIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Expand</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change color</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default StepDetailsPanel;
